import { Box, Button, ButtonGroup, CircularProgress, Dialog, Drawer, IconButton, MenuItem, Popover, Select, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLiveSessionStore } from '../../store/livesessions.store'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Icons, convertToCDNLink, getFileSizeFromBytes, isIOS, formatDate } from '../../utils/utilities'
import { useUserStore } from '../../store/user.store'
import { observer } from 'mobx-react'
import TableHeader from '../../components/common/TableHeader'
import VideoPlayer from '../../components/common/video-player/VideoPlayer'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import CreateClassSessionDialog from '../../components/presenter/live-session/CreateClassSessionDialog'
import { CheckBox, CheckBoxOutlineBlank, ContentCopyOutlined } from '@mui/icons-material'
import AddResourcesDialog from '../../components/presenter/live-session/AddResourcesDialog'
import { toast } from 'react-toastify'
import api from '../../service/api'
import DocumentViewer from '../../components/common/DocumentViewer'

let latestMonth

const columns = [
    { name: "TITLE", align: "left", padding: "10px 20px 10px 40px" },
    { name: "Resource type", align: "left" },
    { name: "resource size", align: "left" },
    { name: "enabled on", align: "left" },
    { name: "Actions", align: "right", padding: "10px 40px 10px 20px" },
]

let newMonth = []

const typeIcons = {
    'document': Icons.default.Document,
    'video': Icons.default.Video,
    'link': Icons.default.Link
}


const ClassSession = observer( () => {

    const [loading, setLoading] = useState( true )
    const [videoSrc, setVideoSrc] = useState( null )
    const [enabling, setEnabling] = useState( false )
    const [previewFile, setPreviewFile] = useState( null )
    const [previewingFileDetails, setPreviewingFileDetails] = useState( null )
    const [addOptionsOpen, setAddOptionsOpen] = useState( false )
    const [selectedResource, setSelectedResource] = useState( null )
    const [selectedAddOption, setSelectedAddOption] = useState( 0 )
    const [videoPlayerControls, setVideoPlayerControls] = useState( {} )
    const [videoPlayerDefaults, setVideoPlayerDefaults] = useState( {} )
    const [editNewDialogState, setEditNewDialogState] = useState( false )
    const [uploadResourcesDialogState, setUploadResourcesDialogState] = useState( false )

    const [searchParams, setSearchParams] = useSearchParams( { filter: 'IN CLASS' } )
    const filter = searchParams.get( 'filter' )

    const setFilter = ( filter ) => {
        setSearchParams( prev => {
            prev.set( 'filter', filter )
            return prev
        }, { replace: true } )
    }

    const LiveSessionStore = useLiveSessionStore()
    const UserStore = useUserStore()

    const addOptionsRef = useRef()

    const navigate = useNavigate()

    const { palette, border, table, theme, shadows } = useTheme()

    const { session_id, subject_code } = useParams()

    const addOptions = [{ icon: Icons.default.AddCircleIcon, label: "Upload resources", action: () => { console.log( "UPLOAD" ); setUploadResourcesDialogState( true ) }, value: "upload" }, { icon: Icons.default.DownloadForOfflineIcon, label: "Import resources", action: () => { console.log( "UPLOAD" ); setUploadResourcesDialogState( true ) }, value: 'import' }]

    const navigateBack = () => {
        navigate( `/${UserStore.getUser.user_role === 'STUDENT' ? 'student' : 'faculty'}/subject/${subject_code}/class-sessions` )
    }

    const handleClose = ( event ) => {
        if ( addOptionsRef.current && addOptionsRef.current.contains( event.target ) ) {
            return
        }
        setAddOptionsOpen( false )
    }

    const handleMenuItemClick = ( event, index, action ) => {
        setSelectedAddOption( index )
        setAddOptionsOpen( false )
        if ( action ) action()
    }

    const enableResource = async ( id ) => {
        setEnabling( prev => {
            const newVals = { ...prev }
            newVals[id] = true
            return newVals
        } )
        await LiveSessionStore.enableResource( session_id, id )
        setEnabling( prev => {
            const newVals = { ...prev }
            newVals[id] = false
            return newVals
        } )
    }

    const disableResource = async ( id ) => {
        setEnabling( prev => {
            const newVals = { ...prev }
            newVals[id] = true
            return newVals
        } )
        await LiveSessionStore.disableResource( session_id, id )
        setEnabling( prev => {
            const newVals = { ...prev }
            newVals[id] = false
            return newVals
        } )
    }

    const unlinkResource = async ( id ) => {
        await LiveSessionStore.unlinkResource( session_id, id )
    }

    const handleDragStart = e => {
        e.currentTarget.style.opacity = 1
    }

    const downloadResource = async ( key, relation_id, details ) => {
        await LiveSessionStore.downloadResource( key, session_id, relation_id, details )
    }


    const copyLink = ( url ) => {
        window.navigator.clipboard.writeText( convertToCDNLink( url ) )
        toast( "Copied link successfully" )
    }

    const handleClickOnVideoResource = async ( resource ) => {
        try {
            const { data: { data } } = await api.liveSessions.fetchLiveSessionResourceLink( session_id, resource.relation_id, { key: resource.resource_key } )

            if ( UserStore.getUser.user_role === 'STUDENT' && filter === 'IN CLASS' ) {
                setVideoPlayerControls( { volume: false } )
                setVideoPlayerDefaults( { volume: 0 } )
            } else {
                setVideoPlayerControls( {} )
                setVideoPlayerDefaults( {} )
            }
            setVideoSrc( convertToCDNLink( data ) )
            setSelectedResource( resource )
        } catch ( err ) {
            toast( err?.responce?.data?.message || "Couldn't fetch session resource!" )
        }
    }

    const viewResource = async ( resource, ext ) => {
        try {
            const { data: { data } } = await api.liveSessions.fetchLiveSessionResourceLink( session_id, resource.relation_id, { key: resource.resource_key } )
            setPreviewFile( convertToCDNLink( data ) )
            setPreviewingFileDetails( { name: resource.name, type: ext } )
        } catch ( err ) {
            console.log( err )
            toast( err?.responce?.data?.message || "Couldn't fetch session resource!" )
        }
    }

    const handleClickOnTheCard = ( resource, ext ) => {
        if ( resource.resource_type === 'link' ) {
            window.open( resource.resource_key )
        } else if ( resource.resource_type === 'video' ) {
            handleClickOnVideoResource( resource )
        } else if ( ( resource.resource_type === 'image' || resource.resource_type === 'document' ) && ( !isIOS() || ( isIOS() && ext === 'pdf' ) ) ) {
            viewResource( resource, ext )
        }
    }


    const getData = useCallback( async () => {
        setLoading( true )
        const { accessDenied } = await LiveSessionStore.fetchLiveSession( session_id )
        if ( accessDenied ) {
            const userRole = UserStore.getUser.user_role === 'STUDENT' ? 'student' : "faculty"
            navigate( `/${userRole}/subject/${subject_code}/class-sessions` )
        }
        setLoading( false )
    }, [LiveSessionStore, session_id, UserStore, subject_code, navigate] )

    useEffect( () => {
        const container = document.getElementById( 'resource-container' )
        if ( container ) {
            container.scrollTo( 0, container.scrollHeight + 200 )
        }
    }, [filter] )



    useEffect( () => {
        getData()
    }, [getData] )

    return (
        <Box flexGrow={1} display="flex" position="relative" overflow="auto" flexDirection="column">
            {!['STUDENT'].includes( UserStore.getUser.user_role ) && <CreateClassSessionDialog mode={editNewDialogState ? 'edit' : 'create'} session={LiveSessionStore?.getLiveSession?.details} setState={() => setEditNewDialogState( false )} state={editNewDialogState} />}
            {!['STUDENT'].includes( UserStore.getUser.user_role ) && selectedAddOption >= 0 && <AddResourcesDialog setLinkMode={setSelectedAddOption} initialMode={addOptions[selectedAddOption].value} filter={filter} session={LiveSessionStore?.getLiveSession?.details} setState={setUploadResourcesDialogState} state={uploadResourcesDialogState} />}
            <Dialog open={Boolean( videoSrc && selectedResource )} onClose={() => { setVideoSrc( null ); setSelectedResource( null ) }}>
                {
                    videoSrc && selectedResource &&
                    <VideoPlayer
                        src={videoSrc}
                        title={selectedResource.name}
                        controllerOptions={videoPlayerControls}
                        defaults={videoPlayerDefaults}
                    />}
            </Dialog>
            <Drawer anchor='bottom' sx={{ zIndex: 9000000 }} open={Boolean( previewFile )} slotProps={{
                paper: { sx: { height: "calc(100% - 50px)" } }
            }} >
                <Box bgcolor={palette.cardBackground} flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                    <Box borderBottom={border[1]} alignItems="center" display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Typography variant='h6'>{previewingFileDetails?.name}</Typography>
                        <Box display="flex" gap="10px" alignItems="center">
                            <IconButton onClick={() => { setPreviewFile( null ); setPreviewingFileDetails( null ) }}>
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>
                    </Box>
                    <Box padding="20px" display="flex" flexDirection="column" overflow="auto" flexGrow={1} height="300px">
                        <DocumentViewer loadingText="Loading material..." setFile={setPreviewFile} file={previewFile} type={previewingFileDetails?.type} />
                    </Box>
                </Box>
            </Drawer>
            <Box display="flex" flexWrap="wrap" justifyContent="space-between" borderBottom="1px solid #d3d3d3" alignItems="center" gap="10px" padding="10px 20px" >
                <Box display="flex" gap="10px" alignItems="center">
                    <IconButton size="small" onClick={navigateBack}>
                        {Icons.default.ArrowBack}
                    </IconButton>
                    <Box>
                        <Typography variant="h6" color='primaryDark.main' fontSize="18px">{LiveSessionStore?.getLiveSession?.details?.name || "Session"}</Typography>
                        <Select size="small" sx={{ fontSize: "12px", background: "none", paddingLeft: "5px", border: `1px solid ${palette.borderColor}` }} variant='standard' value={filter} onChange={e => setFilter( e.target.value )}>
                            <MenuItem dense sx={{ fontSize: "12px" }} value="IN CLASS">In Class</MenuItem>
                            <MenuItem dense sx={{ fontSize: "12px" }} value="BEYOND CLASS">Beyond Class</MenuItem>
                        </Select>
                    </Box>
                </Box>
                {UserStore.getUser.user_role === 'STUDENT' && <IconButton size='small' onClick={getData} >{loading ? <CircularProgress size={20} /> : Icons.default.Replay}</IconButton>}
                {UserStore.getUser?.user_role !== "STUDENT" && <Box display="flex" gap="20px" alignItems="center">
                    <Button onClick={() => { setEditNewDialogState( true ) }} variant="contained" disableElevation startIcon={Icons.default.EditIcon} sx={{ textTransform: "capitalize" }} color="secondary">
                        Edit
                    </Button>

                    <ButtonGroup ref={addOptionsRef} color='primary' variant='contained' disableElevation>
                        <Button onClick={addOptions[selectedAddOption].action} variant="contained" disableElevation startIcon={Icons.default.AddCircleIcon} sx={{ textTransform: "capitalize" }} color="primary">
                            {addOptions[selectedAddOption]['label']}
                        </Button>
                        <Button
                            size="small"
                            aria-controls={addOptionsOpen ? 'split-button-menu' : undefined}
                            aria-expanded={addOptionsOpen ? 'true' : undefined}
                            aria-label="select merge strategy"
                            aria-haspopup="menu"
                            onClick={() => setAddOptionsOpen( !addOptionsOpen )}
                        >
                            {Icons.default.DropDown}
                        </Button>
                    </ButtonGroup>

                    <Popover slotProps={{ paper: { sx: { padding: "0", width: "fit-content" } } }} onClose={handleClose} open={addOptionsOpen} anchorEl={addOptionsRef.current}>
                        <Box >
                            <Box sx={{ padding: "0" }} display="flex" flexDirection="column" id="split-button-menu">
                                {addOptions.map( ( option, index ) => (
                                    <Button
                                        startIcon={option.icon}
                                        variant='contained'
                                        disableElevation
                                        sx={{ display: "inline-flex", borderRadius: "0", textTransform: "capitalize" }}
                                        color="primary"
                                        key={option.label}
                                        onClick={( event ) => handleMenuItemClick( event, index, option.action )}
                                    >
                                        {option.label}
                                    </Button>
                                ) )}
                            </Box>
                        </Box>
                    </Popover>


                </Box>}
            </Box>
            {!loading && UserStore.getUser?.user_role !== 'STUDENT' && <Box flexGrow={1} overflow="auto" display="flex" flexDirection="column">
                {LiveSessionStore.getLiveSession?.resources?.filter( r => r.resource_category === filter )?.length > 0 && <Box bgcolor={palette.contentBg} margin="20px" border={border[1]} borderRadius="5px" flexGrow={1} display="flex" flexDirection='column' overflow="auto" >

                    <TableContainer sx={{ minWidth: "750px", flexGrow: 1, overflow: "auto", borderRadius: "5px" }}>
                        <Table stickyHeader>
                            <TableHeader columns={columns} />
                            <TableBody>
                                {LiveSessionStore.getLiveSession?.resources?.filter( r => r.resource_category === filter )?.map( ( resource, index ) => {
                                    let ext
                                    if ( resource.resource_type === 'document' ) {
                                        ext = resource?.resource_metadata?.originalname?.split( "." ).pop()
                                        ext = ext.toLowerCase()
                                    }

                                    return (
                                        <TableRow onDragStart={handleDragStart} draggable className='table-row draggable-ele' sx={{ "&:hover": { background: palette.rowHover }, cursor: "pointer" }} key={resource.relation_id}>
                                            <TableCell sx={{ border: "none", color: table.color, bgcolor: palette.contentBg, padding: "0" }} align={columns[0].align || 'left'} >
                                                <div>
                                                    <Box padding={columns[0].padding || "10px 20px"} display="flex" alignItems="center" gap="10px">
                                                        {!enabling[resource.relation_id] && !resource.is_enabled && <Tooltip title="Enable resource for students">
                                                            <CheckBoxOutlineBlank fontSize='small' onClick={() => enableResource( resource.relation_id )} color="greyed" />
                                                        </Tooltip>}
                                                        {!enabling[resource.relation_id] && resource.is_enabled && <Tooltip title="Enabled!">
                                                            <CheckBox onClick={() => disableResource( resource.relation_id )} fontSize='small' color="success" />
                                                        </Tooltip>}
                                                        {enabling[resource.relation_id] && <CircularProgress size={20} />}
                                                        <Typography title={resource.name} maxWidth="200px" fontSize="14px" noWrap>
                                                            <strong>
                                                                {resource.name}
                                                            </strong>
                                                        </Typography>
                                                    </Box>
                                                </div>
                                            </TableCell>
                                            <TableCell sx={{ border: "none", color: table.color, bgcolor: palette.contentBg, padding: "0" }} align={columns[1].align || 'left'} >
                                                <Box padding={columns[1].padding || "10px 20px"} display="flex" alignItems="center" gap="10px">
                                                    <strong>
                                                        {resource.resource_type}
                                                    </strong>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ border: "none", color: table.color, bgcolor: palette.contentBg, padding: "0" }} align={columns[2].align || 'left'} >
                                                <Box padding={columns[2].padding || "10px 20px"} display="flex" alignItems="center" gap="10px">
                                                    <strong>
                                                        {resource.resource_type !== 'link' ? getFileSizeFromBytes( resource?.resource_metadata?.size || 0 ) : "NA"}
                                                    </strong>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ border: "none", color: table.color, bgcolor: palette.contentBg, padding: "0" }} align={columns[3].align || 'left'} >
                                                <Box padding={columns[3].padding || "10px 20px"} display="flex" alignItems="center" gap="10px">
                                                    <strong>
                                                        {resource.is_enabled ? formatDate( resource.enabled_at ) : "Not yet enabled"}
                                                    </strong>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ border: "none", color: table.color, bgcolor: palette.contentBg, padding: "0" }} align={columns[4].align || 'left'} >
                                                <Box padding={columns[4].padding || "10px 20px"} display="flex" justifyContent="right" alignItems="center" gap="10px">
                                                    {resource.resource_type === 'link' && <Tooltip title="Visit site">
                                                        <Link target='_blank' to={`${resource.resource_key}`} >
                                                            <IconButton>
                                                                {Icons.default.OpenInNewIcon}
                                                            </IconButton>
                                                        </Link>
                                                    </Tooltip>}
                                                    {resource.resource_type === 'link' && <Tooltip title="Copy link">
                                                        <span>
                                                            <IconButton onClick={() => copyLink( resource.resource_key )}>
                                                                <ContentCopyOutlined />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>}
                                                    {( resource.resource_type === 'image' || resource.resource_type === 'document' || resource.resource_type === 'video' ) && <IconButton onClick={() => downloadResource( resource.resource_key, resource.relation_id, resource )}>
                                                        {Icons.default.DownloadForOfflineIcon}
                                                    </IconButton>}
                                                    {resource.resource_type === 'video' && <IconButton onClick={() => handleClickOnVideoResource( resource )} >
                                                        {Icons.default.PlayCircleFilledWhiteIconOutlined}
                                                    </IconButton>}
                                                    {( ( resource.resource_type === 'image' || resource.resource_type === 'document' ) && ( !isIOS() || ( isIOS() && ext === 'pdf' ) ) ) && <IconButton onClick={() => viewResource( resource, ext )}>
                                                        {Icons.default.VisibilityIcon}
                                                    </IconButton>}
                                                    <Tooltip title="Remove this resource">
                                                        <IconButton onClick={() => { unlinkResource( resource.relation_id ) }} color="error">{Icons.default.ClearIcon}</IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )
                                } )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>}
                {!loading && LiveSessionStore?.getLiveSession?.resources?.filter( r => r.resource_category === filter )?.length === 0 && <Box bgcolor={palette.contentBg} flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" justifyContent="center" gap="20px">
                    {/* <img src={noSessionsImg} width="150px" alt="No quizes" /> */}
                    <Typography variant="subtitle2">You haven't added any resources to '<em>{filter.toLowerCase()}</em>' sessions!</Typography>
                    {UserStore.getUser?.user_role !== "STUDENT" && <Button onClick={addOptions[selectedAddOption].action} startIcon={Icons.default.AddIcon} variant='outlined' color="primaryDark" sx={{ textTransform: "capitalize" }} >Add resources</Button>}
                </Box>}
            </Box>}
            {!loading && UserStore.getUser?.user_role === 'STUDENT' && <Box overflow="auto" className="custom-scrollbar" flexGrow={1} bgcolor={palette.greyedOut} display="flex" flexDirection="column">
                {LiveSessionStore.getLiveSession?.resources?.filter( r => r.resource_category === filter )?.length > 0 && <Box id="resource-container" className="custom-scrollbar" margin="20px" alignItems="center" flexGrow={1} display="flex" gap="10px" flexDirection='column' overflow="auto" >
                    {LiveSessionStore.getLiveSession?.resources?.filter( r => r.resource_category === filter ).map( ( resource, index ) => {

                        const monthAndYear = new Date( resource?.enabled_at || resource?.added_at ).toLocaleDateString( 'en-IN', { month: "long", year: "numeric" } )

                        if ( monthAndYear !== latestMonth ) {
                            newMonth[index] = true
                            latestMonth = monthAndYear
                        }
                        let ext
                        if ( resource.resource_type === 'document' ) {
                            ext = resource?.resource_metadata?.originalname?.split( "." ).pop()
                            ext = ext.toLowerCase()
                        }



                        return (
                            <Box key={resource.relation_id} width="90%" display="flex" flexDirection="column" alignItems="center" justifyContent="center" minWidth="200px" maxWidth="700px" >
                                {newMonth[index] && <Typography borderRadius="5px" marginBottom="20px" padding="5px 20px" bgcolor={palette.secondary.main} color="white">
                                    {monthAndYear}
                                </Typography>}


                                <Box color={resource.is_enabled ? "black" : "DarkGrey"} position="relative" sx={{ cursor: resource.is_enabled ? "pointer" : "not-allowed", '&:hover': { boxShadow: resource.is_enabled && shadows[1] } }} display="flex" gap="10px" padding="10px" width="100%" borderRadius="5px" bgcolor={palette.background.default}>
                                    <Box onClick={() => handleClickOnTheCard( resource, ext )} padding={0} borderRadius="5px" width="160px" height="95px" sx={{ overflow: "hidden", opacity: resource.is_enabled ? 1 : 0.5 }} bgcolor={theme === 'dark' ? palette.grey[600] : palette.grey[200]} display="flex" alignItems="center" justifyContent="center">
                                        {resource?.resource_metadata?.resource?.image ? <img height="100%" style={{ objectFit: "contain" }} width="100%" src={resource?.resource_metadata?.resource?.image} alt={resource?.name} /> : typeIcons[resource.resource_type]}
                                    </Box>
                                    <Box flexGrow="1" display="flex" flexWrap="wrap">
                                        <Box flexGrow={1} onClick={() => handleClickOnTheCard( resource, ext )} alignItems="flex-start">
                                            <Typography minWidth="0" maxWidth="200px" variant="body2" noWrap fontSize="16px">
                                                {resource.name}
                                            </Typography>
                                            <Box marginTop="10px">
                                                <Typography display="inline" variant="subtitle2" color={resource.is_enabled ? "textSecondary" : "inherit"} marginTop="-5px">{resource.resource_type}</Typography>
                                                <Typography marginLeft="10px" display="inline" variant="body2" marginTop="5px" color={resource.is_enabled ? "GrayText" : "inherit"} fontSize="12px">{resource.resource_type === 'link' ? "" : getFileSizeFromBytes( resource.resource_metadata?.size )}</Typography>
                                            </Box>
                                        </Box>
                                        <Box display="flex" alignItems="flex-start" justifyContent="flex-end">
                                            {resource.resource_type === 'link' && <Tooltip title="Visit site">
                                                <Link target='_blank' to={`${resource.resource_key}`} >
                                                    <IconButton disabled={!resource.is_enabled}>
                                                        {Icons.default.OpenInNewIcon}
                                                    </IconButton>
                                                </Link>
                                            </Tooltip>}
                                            {resource.resource_type === 'link' && <Tooltip title="Copy link">
                                                <span>
                                                    <IconButton onClick={() => copyLink( resource.resource_key )} disabled={!resource.is_enabled}>
                                                        <ContentCopyOutlined />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>}
                                            {resource.resource_type === 'video' && <IconButton onClick={() => handleClickOnVideoResource( resource )} disabled={!resource.is_enabled}>
                                                {Icons.default.PlayCircleFilledWhiteIconOutlined}
                                            </IconButton>}
                                            {( ( resource.resource_type === 'image' || resource.resource_type === 'document' ) && ( !isIOS() || ( isIOS() && ext === 'pdf' ) ) ) && <IconButton disabled={!resource.is_enabled} onClick={() => viewResource( resource, ext )}>
                                                {Icons.default.VisibilityIcon}
                                            </IconButton>}
                                            {( resource.resource_type === 'image' || resource.resource_type === 'document' || resource.resource_type === 'video' ) && <IconButton onClick={() => downloadResource( resource.resource_key, resource.relation_id, resource )} disabled={!resource.is_enabled}>
                                                {Icons.default.DownloadForOfflineIcon}
                                            </IconButton>}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )
                    } )}
                </Box>}
                {!loading && LiveSessionStore?.getLiveSession?.resources?.filter( r => r.resource_category === filter )?.length === 0 && <Box bgcolor={palette.contentBg} flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" justifyContent="center" gap="20px">
                    {/* <img src={noSessionsImg} width="150px" alt="No quizes" /> */}
                    <Typography variant="subtitle2">Resources are not added to this session yet!</Typography>
                    {UserStore.getUser?.user_role !== "STUDENT" && <Button onClick={addOptions[selectedAddOption].action} startIcon={Icons.default.AddIcon} variant='outlined' color="primaryDark" sx={{ textTransform: "capitalize" }} >Create one</Button>}
                </Box>}
            </Box>}
            {loading && <DataLoadingSpinner waitingMessage="Loading resources..." />}
        </Box>
    )
} )

export default ClassSession
